import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";

import "./styles.css";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useParams } from "react-router-dom";

import { pathToLogoComplete } from "../common/assets";
import { Invoice } from "../admin/types";
import api from "../api/api";

import dayjs from "../common/date";
import { formatCurrencyAmount } from "../common/utils";
import Loading from "../common/components/Loading";
import { backgrounds } from "../theme/theme";

const formatDateForInvoice = (dateStr: string) => {
  return dayjs(dateStr).utc().format("MMM D, YYYY");
};

function InvoicesView() {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    if (id) {
      api.users
        .fetchInvoice(id)
        .then((_invoice: Invoice) => {
          setInvoice(_invoice);
        })
        .catch(() => {
          //
        });
    }
  }, [id]);

  if (!invoice) {
    return <Loading />;
  }
  document.title = `${invoice.publicId} - DoneStreet`;

  const subtotal =
    invoice.totalPaymentAmount -
    invoice.secondaryItemAmount -
    invoice.salesTaxAmount;

  return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: backgrounds.page,
          py: 4,
          px: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: 800,
            mx: "auto",
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
              mb: 4,
            }}
          >
            <Box>
              <img
                src={pathToLogoComplete}
                alt="DoneStreet Logo"
                style={{ width: 120, marginBottom: 8 }}
              />
            </Box>
            <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
              <Typography variant="h5" fontWeight={600} color="primary">
                INVOICE
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                #{invoice.publicId}
              </Typography>
              {invoice.paidAt ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Paid"
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              ) : (
                <Chip
                  icon={<ScheduleIcon />}
                  label="Pending"
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Dates and Parties */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={600}
              >
                Bill To
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                DoneStreet Inc.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                412 - 1288 Ritson Road North
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Oshawa ON L1G 7Z9
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Canada
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={600}
              >
                From
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                {invoice.user.firstName} {invoice.user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {invoice.user.emailAddress}
              </Typography>
            </Grid>
          </Grid>

          {/* Invoice Details */}
          <Box
            sx={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              mb: 4,
              p: 2,
              backgroundColor: backgrounds.surface,
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Issue Date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatDateForInvoice(invoice.createdAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Due Date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatDateForInvoice(invoice.paymentDueAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Payment Method
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                Online Transfer
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Currency
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {invoice.currency}
              </Typography>
            </Box>
          </Box>

          {/* Line Items */}
          <TableContainer sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">
                      {invoice.totalNumberOfHours} hrs of Software Development
                      services
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateForInvoice(invoice.periodStartDate)} –{" "}
                      {formatDateForInvoice(invoice.periodEndDate)} •{" "}
                      {invoice.engagement.clientName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrencyAmount(subtotal, invoice.currency)}
                    </Typography>
                  </TableCell>
                </TableRow>
                {invoice.secondaryItemAmount &&
                invoice.secondaryItemDescription ? (
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.secondaryItemDescription}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrencyAmount(
                          invoice.secondaryItemAmount,
                          invoice.currency
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: 280 },
                backgroundColor: backgrounds.surface,
                borderRadius: 1,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">
                  {formatCurrencyAmount(
                    subtotal + (invoice.secondaryItemAmount || 0),
                    invoice.currency
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Sales Tax
                </Typography>
                <Typography variant="body2">
                  {formatCurrencyAmount(invoice.salesTaxAmount, invoice.currency)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body1" fontWeight={600}>
                  Total
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  {formatCurrencyAmount(
                    invoice.totalPaymentAmount,
                    invoice.currency
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
  );
}

export default InvoicesView;
